import { ApiError, adminAuthHeaders, api, type MoneyJson } from '../../api';

/**
 * The administration API, as the Mini App speaks it.
 *
 * These are the *same* routes `apps/admin` drives — `/api/admin/products`,
 * `/api/admin/categories`, `/api/admin/inventory` — because there is one product
 * table, one variant system and one publication rule. Nothing here creates a
 * second catalogue.
 *
 * The transport is a copy of the one in `src/api.ts` and wants to be deleted:
 * that file belongs to another agent this round, so an admin call could not be
 * added to it. It carries the same three things, for the same reasons — the
 * bearer token because iOS drops the cross-site cookie, the CSRF echo because
 * the server demands it on every write, and `ApiError` so a screen can show what
 * the server actually said. The report asks for this to be folded back in.
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

/*
 * Credentials come from the management session, not the customer's.
 *
 * This module used to be handed the customer's tokens by the admin root as it
 * rendered, and every request under `/api/admin` answered «Недостаточно прав».
 * That was not a role problem. `apps/api/src/app.ts` resolves a session under
 * the scope the URL implies — `admin` for `/api/admin`, `miniapp` for the rest —
 * and `resolveSession` filters on that scope, so the customer's token is not
 * weaker there, it is invisible: the server finds no session and refuses before
 * any permission is read.
 *
 * `adminApi.signIn` mints the admin-scoped session from the same signed
 * `initData`, and this reads its headers. Until that has succeeded the headers
 * are empty and the request is refused by the server rather than by a guess
 * here.
 */

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  Object.assign(headers, adminAuthHeaders());

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    /* `omit`, deliberately. The customer's cookie is scoped to `miniapp` and
       would be sent alongside the admin bearer token; the server would resolve
       neither and answer 401. The bearer above is the whole credential. */
    credentials: 'omit',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: unknown } })
      .error;
    throw new ApiError(
      error?.code ?? 'internal_error',
      // The server's own sentence, whenever it sent one. A screen that replaces
      // it with «Что-то пошло не так» is the specific complaint this round
      // exists to answer, and it starts here.
      error?.message ?? 'Не удалось выполнить запрос',
      response.status,
      error?.details,
    );
  }

  return payload as T;
}

/**
 * A key that makes a repeated write the same write.
 *
 * Carried in the *body*, not in a header: the API's CORS policy names its
 * allowed request headers explicitly (`apps/api/src/app.ts`, `allowedHeaders`),
 * and the Mini App is served from a different host to the API, so a header the
 * list does not mention fails the preflight and takes the whole request with it.
 * An unknown body key is dropped by zod's default strip, so this is inert
 * against today's server and live the moment the backend reads it.
 */
export const newIdempotencyKey = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// --- the wire ---------------------------------------------------------------

export type ProductStatus = 'draft' | 'published' | 'hidden' | 'archived';
export type FulfillmentMode = 'automatic' | 'manual' | 'none';

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  draft: 'Черновик',
  published: 'Опубликован',
  hidden: 'Скрыт',
  archived: 'В архиве',
};

export const FULFILLMENT_LABELS: Record<FulfillmentMode, string> = {
  automatic: 'Автоматически из склада',
  manual: 'Вручную оператором',
  none: 'Без выдачи',
};

/** The item types `INVENTORY_ITEM_TYPES` in `@store/shared` allows. */
export const INVENTORY_TYPES = [
  { value: 'license_key', label: 'Ключ активации' },
  { value: 'activation_code', label: 'Код активации' },
  { value: 'gift_code', label: 'Подарочный код' },
  { value: 'activation_url', label: 'Ссылка активации' },
  { value: 'text_payload', label: 'Текст' },
  { value: 'manual_task', label: 'Задача оператору' },
] as const;

export type InventoryType = (typeof INVENTORY_TYPES)[number]['value'];

export interface AdminVariantRow {
  readonly id: string;
  readonly title: string;
  readonly available: number | null;
}

/** One row of `GET /api/admin/products`. */
export interface AdminProductRow {
  readonly id: string;
  readonly shortId: string;
  readonly title: string;
  readonly status: ProductStatus;
  readonly basePrice: MoneyJson;
  readonly tracksInventory: boolean;
  readonly fulfillmentMode: FulfillmentMode;
  readonly lowStockThreshold: number;
  readonly categoryId: string | null;
  /** Null means the product does not count stock at all — not "none left". */
  readonly available: number | null;
  readonly variants: readonly AdminVariantRow[];
  /**
   * The first picture, when the route sends one. It does not today: the list
   * projects the product row and never joins `product_media`. Optional rather
   * than assumed, so the list degrades to a monogram instead of a broken tile.
   */
  readonly imageUrl?: string | null;
  readonly subtitle?: string | null;
}

export interface AdminVariantDetail {
  readonly id: string;
  readonly title: string;
  readonly price: MoneyJson;
  readonly isPublished: boolean;
  readonly durationDays: number | null;
  readonly sortOrder?: number;
  readonly tracksInventory?: boolean;
  readonly available: number | null;
}

export interface AdminMedia {
  readonly url: string;
  readonly alt: string | null;
  readonly sortOrder: number;
}

/**
 * Everything the editor needs about one product.
 *
 * The optional members are the ones only `GET /products/:id` returns, so a
 * detail composed from the list (see `composeFromCatalog`) is missing them and
 * says so through `partial`.
 *
 * `badge` and `sku` used to be here and are gone: neither has a column on
 * `products`, so the editor offered two fields that the server accepted with a
 * 200 and threw away. A control that cannot save is worse than an absent one —
 * the owner types a value, sees success, and finds it missing later.
 */
export interface AdminProductDetail {
  readonly id: string;
  readonly shortId: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly description: string | null;
  readonly activationInstructions: string | null;
  readonly categoryId: string | null;
  readonly basePrice: MoneyJson;
  readonly listPrice: MoneyJson | null;
  readonly status: ProductStatus;
  readonly fulfillmentMode: FulfillmentMode;
  readonly tracksInventory: boolean;
  readonly lowStockThreshold?: number;
  readonly purchaseLimitPerUser?: number | null;
  readonly sortOrder?: number;
  readonly isFeatured?: boolean;
  readonly attributes?: Record<string, string>;
  readonly media?: readonly AdminMedia[];
  readonly variants?: readonly AdminVariantDetail[];
  readonly available: number | null;
  /**
   * True when this was assembled from the catalogue rather than read whole.
   *
   * The editor says so out loud: on that path several fields simply did not
   * arrive, and an empty box the owner reads as "not set" is how a purchase
   * limit gets deleted by somebody who was editing the title.
   */
  readonly partial?: boolean;
}

export interface AdminCategory {
  readonly id: string;
  readonly shortId: string;
  readonly parentId: string | null;
  readonly slug: string;
  readonly title: string;
  readonly description: string | null;
  readonly iconEmoji: string | null;
  readonly imageUrl: string | null;
  readonly sortOrder: number;
  readonly isPublished: boolean;
}

/** `inventoryService.previewImport`, verbatim. */
export interface ImportPreview {
  readonly total: number;
  readonly unique: number;
  readonly duplicatesInFile: number;
  readonly alreadyInStock: number;
  readonly invalid: number;
  /** Masked server-side; nothing here is a sellable value. */
  readonly samples: readonly string[];
}

export interface ImportResult {
  readonly batchId: string;
  readonly imported: number;
  readonly skipped: number;
  readonly duplicates: number;
  readonly invalid: number;
}

/** What `POST`/`PATCH /products` accepts, plus the fields the report asks for. */
export interface ProductWrite {
  title?: string;
  subtitle?: string | null;
  description?: string | null;
  activationInstructions?: string | null;
  categoryId?: string | null;
  basePrice?: string;
  listPrice?: string | null;
  baseCurrency?: string;
  status?: ProductStatus;
  fulfillmentMode?: FulfillmentMode;
  tracksInventory?: boolean;
  lowStockThreshold?: number;
  purchaseLimitPerUser?: number | null;
  sortOrder?: number;
  isFeatured?: boolean;
  attributes?: Record<string, string>;
  media?: Array<{ url: string; alt: string | null; sortOrder: number }>;
}

export interface VariantWrite {
  title?: string;
  price?: string;
  currency?: string;
  durationDays?: number | null;
  isPublished?: boolean;
  sortOrder?: number;
  tracksInventory?: boolean;
}

export interface CategoryWrite {
  title?: string;
  description?: string | null;
  iconEmoji?: string | null;
  imageUrl?: string | null;
  sortOrder?: number;
  parentId?: string | null;
  isPublished?: boolean;
}

// --- calls ------------------------------------------------------------------

/**
 * The full product, read whole when the server can, assembled when it cannot.
 *
 * `GET /api/admin/products/:id` does not exist yet — the admin API has a list
 * and a PATCH and nothing between them — so the editor would have no way to open
 * an existing product at all. The fallback composes the list row (status, stock
 * mode, category, delivery) with the public catalogue detail (описание,
 * инструкция, варианты, картинки), which is complete enough to edit a published
 * product today and is marked `partial` so the form can say what it is missing.
 * A draft has no public detail, so that case surfaces the server's own error.
 */
export async function adminProduct(productId: string): Promise<AdminProductDetail> {
  try {
    const { product } = await request<{ product: AdminProductDetail }>(
      `/api/admin/products/${encodeURIComponent(productId)}`,
    );
    return product;
  } catch (cause) {
    if (!(cause instanceof ApiError) || (cause.status !== 404 && cause.status !== 405)) throw cause;
    return composeFromCatalog(productId, cause);
  }
}

async function composeFromCatalog(
  productId: string,
  missing: ApiError,
): Promise<AdminProductDetail> {
  const { products } = await adminProducts();
  const row = products.find((candidate) => candidate.id === productId);
  if (!row) throw missing;

  const detail = await api.product(row.shortId).catch(() => null);

  return {
    id: row.id,
    shortId: row.shortId,
    title: row.title,
    subtitle: detail?.product.subtitle ?? null,
    description: detail?.product.description ?? null,
    activationInstructions: detail?.product.activationInstructions ?? null,
    categoryId: row.categoryId,
    basePrice: row.basePrice,
    listPrice: detail?.product.listPrice ?? null,
    status: row.status,
    fulfillmentMode: row.fulfillmentMode,
    tracksInventory: row.tracksInventory,
    lowStockThreshold: row.lowStockThreshold,
    media: (detail?.media ?? [])
      .filter((item) => item.kind === 'image')
      .map((item, index) => ({ url: item.url, alt: item.alt, sortOrder: index })),
    variants: (detail?.variants ?? []).map((variant, index) => ({
      id: variant.id,
      title: variant.title,
      price: variant.price,
      isPublished: true,
      durationDays: variant.durationDays,
      sortOrder: index,
      available: variant.available,
    })),
    available: row.available,
    partial: true,
  };
}

/**
 * The product list, with the page the server actually sent.
 *
 * `GET /products` defaults to `limit: 50` and answers `{ products, total, limit,
 * offset }`. The client asked for none of that and typed only `products`, so a
 * shop with more than fifty of them lost the rest silently — on the one screen
 * that can fix a product. Reading `total` back is what lets the screen say so.
 */
export interface AdminProductPage {
  readonly products: AdminProductRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export const adminProducts = (params: { limit?: number; offset?: number } = {}) => {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return request<AdminProductPage>(`/api/admin/products${suffix}`);
};

/**
 * A copy of a product, made by the server in one transaction.
 *
 * This used to be assembled here: read the source, create a product, then one
 * request per variant. That is N+1 round trips on mobile data, it is not
 * atomic — a variant that fails halfway leaves a half-copied product behind —
 * and it silently dropped everything the create body has no field for, media
 * and reference price among them. The route exists and does all of it at once.
 */
export const duplicateProduct = (productId: string, idempotencyKey: string, title?: string) =>
  request<{ product: { id: string; shortId: string } }>(
    `/api/admin/products/${encodeURIComponent(productId)}/duplicate`,
    { method: 'POST', body: { idempotencyKey, ...(title !== undefined ? { title } : {}) } },
  );

/** What `PATCH /products/:id/status` says back about what it just did. */
export interface StatusChange {
  readonly product: { id: string; shortId: string };
  readonly sellable: boolean;
  readonly blockReason: string | null;
  /**
   * Set only when the owner has just published something a customer cannot
   * complete — `null` on every other transition.
   */
  readonly warning: { code: string | null; message: string | null } | null;
}

/**
 * Publish, hide or archive — through the route built for it.
 *
 * The screens used to send `PATCH /products/:id { status }`, which does change
 * the status and does keep `archived_at` in step. What it cannot do is answer
 * the question that route was added for: whether the thing just published can
 * actually be bought. Publishing an unsellable product is deliberately allowed
 * and reported, and going through the generic PATCH threw the report away — so
 * the owner's original complaint, a published product nobody could buy, stayed
 * exactly as silent as before.
 */
export const setProductStatus = (productId: string, status: ProductStatus, reason?: string) =>
  request<StatusChange>(`/api/admin/products/${encodeURIComponent(productId)}/status`, {
    method: 'PATCH',
    body: { status, ...(reason !== undefined ? { reason } : {}) },
  });

export const createProduct = (body: ProductWrite, idempotencyKey: string) =>
  request<{ product: { id: string; shortId: string } }>('/api/admin/products', {
    method: 'POST',
    body: { ...body, idempotencyKey },
  });

export const patchProduct = (productId: string, body: ProductWrite) =>
  request<{ product: { id: string; shortId: string } }>(
    `/api/admin/products/${encodeURIComponent(productId)}`,
    { method: 'PATCH', body },
  );

export const createVariant = (productId: string, body: VariantWrite, idempotencyKey: string) =>
  request<{ variant: { id: string } }>(
    `/api/admin/products/${encodeURIComponent(productId)}/variants`,
    { method: 'POST', body: { ...body, idempotencyKey } },
  );

export const patchVariant = (productId: string, variantId: string, body: VariantWrite) =>
  request<{ variant: { id: string } }>(
    `/api/admin/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}`,
    { method: 'PATCH', body },
  );

/**
 * Removing a variant is archiving it, and there is no other kind.
 *
 * This was a `DELETE` to the variant's own URL, and no such route has ever
 * existed — `admin.ts` declares no DELETE at all — so taking a variant off a
 * saved product answered 404 and failed the whole save. `PATCH { isArchived }`
 * is the route that does exist, and it is also the only correct shape: an order
 * line references the variant it was bought as, so a row that is really deleted
 * takes a customer's order history with it.
 */
export const archiveVariant = (productId: string, variantId: string) =>
  request<{ variant: { id: string } }>(
    `/api/admin/products/${encodeURIComponent(productId)}/variants/${encodeURIComponent(variantId)}`,
    { method: 'PATCH', body: { isArchived: true } },
  );

export const adminCategories = () =>
  request<{ categories: AdminCategory[] }>('/api/admin/categories');

export const createCategory = (body: CategoryWrite, idempotencyKey: string) =>
  request<{ category: AdminCategory }>('/api/admin/categories', {
    method: 'POST',
    body: { ...body, idempotencyKey },
  });

export const patchCategory = (categoryId: string, body: CategoryWrite) =>
  request<{ category: AdminCategory }>(`/api/admin/categories/${encodeURIComponent(categoryId)}`, {
    method: 'PATCH',
    body,
  });

export interface ImportRequest {
  readonly productId: string;
  readonly variantId: string | null;
  readonly itemType: InventoryType;
  readonly label: string;
  readonly payloads: readonly string[];
}

export const inventoryPreview = (body: ImportRequest) =>
  request<{ preview: ImportPreview }>('/api/admin/inventory/preview', { method: 'POST', body });

export const inventoryImport = (body: ImportRequest, idempotencyKey: string) =>
  request<{ result: ImportResult }>('/api/admin/inventory/import', {
    method: 'POST',
    body: { ...body, idempotencyKey },
  });
